import React, { useState } from 'react';
import {
    Chip,
    Popover,
    Box,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Divider,
    Fade,
    useTheme,
} from '@mui/material';
import { CheckCircle, LocalShipping, Inventory, Home, ErrorOutline, HelpOutline } from '@mui/icons-material';

const EXCEPTION_KEYWORDS = [
    'exception',
    'error',
    'failure',
    'return',
    'failed',
    'returned',
    'delay',
    'held',
    'alert',
    'undeliverable as addressed',
    'undeliverable',
    'returned to sender',
    'addressee unknown',
    'available for pickup',
    'forwarded',
    'held at post office per customer request',
    'held at post office',
    'package intercepted',
    'intercepted',
    'damaged',
    'seized by law enforcement',
    'seized',
    'notice left',
    'delivery attempted',
    'refused',
    'insufficient address',
    'no such number',
    'no access',
];

const getStatus = (event) => {
    const eventLower = event.toLowerCase();

    // Check for exception status first (highest priority)
    if (EXCEPTION_KEYWORDS.some((keyword) => eventLower.includes(keyword))) return 'exception';

    if (eventLower.includes('delivered')) return 'delivered';
    if (eventLower.includes('out for delivery')) return 'out-for-delivery';
    if (eventLower.includes('arrived') || eventLower.includes('departed')) return 'in-transit';
    if (eventLower.includes('accepted')) return 'pre-transit';
    if (eventLower.includes('pre-shipment')) return 'pre-shipment';
    return 'unknown';
};

const getIcon = (status) => {
    switch (status) {
        case 'delivered':
            return <CheckCircle />;
        case 'out-for-delivery':
            return <LocalShipping />;
        case 'in-transit':
            return <Inventory />;
        case 'pre-transit':
        case 'pre-shipment':
            return <Home />;
        case 'exception':
        case 'error':
        case 'failure':
            return <ErrorOutline />;
        default:
            return <HelpOutline />;
    }
};

const getChipProps = (status) => {
    switch (status) {
        case 'delivered':
            return { color: 'success' };
        case 'out-for-delivery':
            return { color: 'info' };
        case 'in-transit':
            return { color: 'warning' };
        case 'pre-transit':
        case 'pre-shipment':
            return { color: 'secondary' };
        case 'exception':
        case 'error':
        case 'failure':
            return { color: 'error' };
        default:
            return { color: 'default' };
    }
};

const getStatusColor = (status, theme) => {
    switch (status) {
        case 'delivered':
            return theme.palette.success.main;
        case 'out-for-delivery':
            return theme.palette.info.main;
        case 'in-transit':
            return theme.palette.warning.main;
        case 'pre-transit':
        case 'pre-shipment':
            return theme.palette.primary.main;
        case 'exception':
        case 'error':
        case 'failure':
            return theme.palette.error.main;
        default:
            return theme.palette.grey[500];
    }
};

const TrackingStatusChip = ({ trackingData }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);

    // Extract latest event and all events
    const events = trackingData?.events || [];
    const latestEvent = events.length > 0 ? events[0]?.Event : 'No tracking info';

    const handleClick = (event) => {
        if (events.length > 0) {
            setAnchorEl(anchorEl ? null : event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const status = events.length > 0 ? getStatus(latestEvent) : 'unknown';
    const chipProps = getChipProps(status);
    const icon = getIcon(status);

    return (
        <>
            <Chip
                label={latestEvent}
                icon={icon}
                {...chipProps}
                onClick={handleClick}
                sx={{
                    width: 'fit-content',
                    maxWidth: '200px',
                    height: 'fit-content',
                    borderRadius: 1,
                    fontSize: 11,
                    padding: '0.4em 0.1em',
                    fontWeight: 'normal',
                    cursor: events.length > 0 ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': {
                        transform: events.length > 0 ? 'scale(1.02)' : 'none',
                        boxShadow: events.length > 0 ? 2 : 0,
                    },
                    '& .MuiChip-icon': {
                        color: 'inherit',
                    },
                    '& .MuiChip-label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}
            />

            <Popover
                sx={{ border: '1px solid rgba(255,255,255,0.1)' }}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 300 }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: 500,
                            minWidth: 400,
                            borderRadius: 2,
                            boxShadow: 6,
                            ml: -1,
                        },
                    },
                }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
                        Tracking Timeline
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                        {events.length === 0 ? (
                            <Box textAlign="center" py={2}>
                                <HelpOutline sx={{ fontSize: 48, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" mt={1}>
                                    No tracking information available
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {events.map((event, index) => {
                                    const eventStatus = getStatus(event.Event);
                                    const statusColor = getStatusColor(eventStatus, theme);
                                    return (
                                        <Fade in={true} timeout={300 + index * 100} key={index}>
                                            <ListItem
                                                alignItems="flex-start"
                                                sx={{
                                                    mb: 1.5,
                                                    borderLeft: 3,
                                                    borderColor: statusColor,
                                                    bgcolor: `${statusColor}10`,
                                                    borderRadius: 1,
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        bgcolor: `${statusColor}20`,
                                                        transform: 'translateX(2px)',
                                                    },
                                                }}>
                                                <ListItemAvatar sx={{ minWidth: 48 }}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: statusColor,
                                                            width: 32,
                                                            height: 32,
                                                        }}>
                                                        {getIcon(eventStatus)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {event.Event}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Box>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                display="block">
                                                                {event.EventDateTime}
                                                            </Typography>
                                                            {event.EventAddress && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {event.EventAddress}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        </Fade>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default TrackingStatusChip;
